// Browser page translators (Firefox built-in, Yandex, Google Translate) rewrite
// text nodes React owns. React 18's commit phase then throws
// "NotFoundError: Node.removeChild: The node to be removed is not a child of
// this node" and the whole app falls into AppErrorBoundary (Sentry 6909999408,
// 31 users in a week on Firefox 157/158 + Yandex). `translate="no"` on <html>
// is already set and ignored by these translators, so guard the DOM calls.

type GuardKind = "removeChild" | "insertBefore";

const TRANSLATOR_TAGS = new Set(["font", "ya-tr-span"]);

let reported = false;
let reporting = false;

const reportOnce = (kind: GuardKind, node: Node) => {
  if (reported || reporting) return;
  reporting = true;
  import("@sentry/react")
    .then((Sentry) => {
      reporting = false;
      // Before Sentry.init (lazy, after first render) a capture is a no-op;
      // leave `reported` unset so a later hit in the session still reports.
      if (!Sentry.getClient?.()) return;
      reported = true;
      Sentry.captureMessage("DOM guard: translation mutated React-owned node", {
        level: "warning",
        tags: { domGuard: kind },
        extra: {
          nodeTag: (node as any)?.tagName ?? node?.nodeName,
          parentTag: (node as any)?.parentElement?.tagName,
          documentLang: document.documentElement?.lang,
          userAgent: navigator.userAgent,
          navigatorLanguage: navigator.language,
        },
      });
    })
    .catch(() => {
      reporting = false;
    });
};

// The direct child of `parent` that now holds `node`, or null when the node
// was moved out of the parent entirely.
const wrapperUnder = (parent: Node, node: Node): Node | null => {
  let el: Node | null = node.parentNode;
  while (el && el.parentNode !== parent) el = el.parentNode;
  return el;
};

// Safe to remove with the node only if it is a translator element or holds
// nothing but the node; a React-owned sibling that a translator moved text
// into must survive.
const isDisposableWrapper = (wrapper: Node, node: Node): boolean => {
  if (TRANSLATOR_TAGS.has((wrapper as any).tagName?.toLowerCase?.()))
    return true;
  let el: Node | null = wrapper;
  while (el && el !== node) {
    if (el.childNodes.length !== 1) return false;
    el = el.firstChild;
  }
  return el === node;
};

export const installDomTranslationGuard = (proto: any = Node.prototype) => {
  if (!proto || proto.__domTranslationGuard) return;
  proto.__domTranslationGuard = true;

  const removeChild = proto.removeChild;
  proto.removeChild = function (child: Node) {
    if (child && child.parentNode !== this) {
      reportOnce("removeChild", child);
      const wrapper = wrapperUnder(this, child);
      if (wrapper && isDisposableWrapper(wrapper, child))
        removeChild.call(this, wrapper);
      return child;
    }
    return removeChild.call(this, child);
  };

  const insertBefore = proto.insertBefore;
  proto.insertBefore = function (node: Node, reference: Node | null) {
    if (reference && reference.parentNode !== this) {
      reportOnce("insertBefore", reference);
      return insertBefore.call(this, node, wrapperUnder(this, reference));
    }
    return insertBefore.call(this, node, reference);
  };
};

// Test hook only.
export const __resetDomTranslationGuardReport = () => {
  reported = false;
  reporting = false;
};
